export type PriorityTone = "critical" | "neutral";

export interface PriorityToken {
  readonly label: string;
  readonly tone: PriorityTone;
}

export interface TaskMetadataInput {
  readonly id: string;
  readonly projectName: string;
  readonly campaignName: string | null;
  readonly teamName: string | null;
}

const PRIORITY_TOKENS: Record<string, PriorityToken> = {
  critical: { label: "P0", tone: "critical" },
  p1: { label: "P1", tone: "critical" },
  urgent: { label: "P0", tone: "critical" },
  high: { label: "P1", tone: "critical" },
  normal: { label: "P2", tone: "neutral" },
  p2: { label: "P2", tone: "neutral" },
  medium: { label: "P2", tone: "neutral" },
  minor: { label: "P3", tone: "neutral" },
  p3: { label: "P3", tone: "neutral" },
  low: { label: "P3", tone: "neutral" },
};

export function formatPriorityToken(priority: string | null | undefined): PriorityToken | null {
  const normalized = priority?.trim().toLowerCase();
  if (!normalized || normalized === "none") {
    return null;
  }

  return PRIORITY_TOKENS[normalized] ?? null;
}

export function formatStatusDisplayLabel(status: string | null | undefined): string {
  return formatSentenceTitleCase(status) || "Unknown";
}

export function formatPriorityDisplayLabel(priority: string | null | undefined): string {
  const normalized = priority?.trim().toLowerCase();
  if (!normalized || normalized === "none") {
    return "None";
  }

  return formatSentenceTitleCase(priority) || "None";
}

export function formatAssigneeDisplayName(
  assigneeId: string | null | undefined,
  members: ReadonlyArray<{ readonly id: string; readonly name: string }>,
): string {
  if (!assigneeId) return "Unassigned";
  return members.find((member) => member.id === assigneeId)?.name || "Unknown assignee";
}

/**
 * A metadata segment, tagged with what it names.
 *
 * The row used to build plain strings, which is why the project segment was
 * the one place a project appeared without its icon — the renderer could not
 * tell which of four look-alike strings was the project.
 */
export interface TaskMetadataSegment {
  readonly kind: "id" | "project" | "campaign" | "team";
  readonly label: string;
}

export function buildTaskMetadataSegments(input: TaskMetadataInput): TaskMetadataSegment[] {
  const segments: TaskMetadataSegment[] = [
    { kind: "id", label: input.id },
    { kind: "project", label: input.projectName },
  ];

  if (input.campaignName && input.campaignName !== input.projectName) {
    segments.push({ kind: "campaign", label: input.campaignName });
  }

  if (input.teamName) {
    segments.push({ kind: "team", label: input.teamName });
  }

  return segments;
}

function formatSentenceTitleCase(value: string | null | undefined): string {
  const normalized = value?.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (!normalized) return "";

  return normalized
    .split(" ")
    .map((part) => {
      if (/^p\d+$/i.test(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
}
